import React, { useState } from 'react';
import { Modal, Button, ListGroup, Badge, Spinner, Alert, Form } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaThermometerHalf, FaTint, FaExclamationTriangle } from 'react-icons/fa';
import { finalizarLotesBulkCalibracion } from '../../../../api/common';
import ValidarPermiso from '../../../Roles/ValidarPermiso';
import { FORZAR_FINALIZAR_LOTE } from '../../../Roles/ConstantRoute';
import { useAuth } from '../../../../hooks/useAuth';
import { Alertassw } from '../../../Alertas/Alertassw';

const ResumenFinalizacion = ({ show, onHide, loteEquiposId, lotesCalibracion, plantilla, onSuccess }) => {
  const { userData } = useAuth();
  const puedeForzar = ValidarPermiso(
    FORZAR_FINALIZAR_LOTE,
    userData?.role?.permisos || []
  ) !== -1;

  const [finalizando, setFinalizando] = useState(false);
  const [error, setError] = useState(null);
  const [omitirValidacion, setOmitirValidacion] = useState(false);
  const [sesionesIncompletas, setSesionesIncompletas] = useState(null);
  const [hojasExistentes, setHojasExistentes] = useState(null);

  const resetErrores = () => {
    setError(null);
    setSesionesIncompletas(null);
    setHojasExistentes(null);
  };

  const handleFinalizar = async () => {
    setFinalizando(true);
    resetErrores();
    try {
      const res = await finalizarLotesBulkCalibracion(loteEquiposId, { omitirValidacion });
      // El backend envuelve como { ok, message, data: {...} } — accedemos al doble nivel.
      const body = res?.data ?? res;
      const data = body?.data ?? body;
      const hojasCount = data?.hojasGeneradas?.length || 0;
      const otsCount = data?.otsAfectadas || 0;
      const equipos = data?.equiposTransitados || 0;
      Alertassw(
        'success',
        'Lote procesado',
        `${equipos} equipo(s) → Procesado, ${hojasCount} hoja(s) generada(s) en ${otsCount} OT(s).`
      );
      // Dar un pequeño delay para que el toast sea visible antes de navegar.
      setTimeout(() => onSuccess(data), 600);
    } catch (err) {
      console.error('Error finalizando lotes:', err);
      const code = err?.response?.data?.error || err?.error;
      const message = err?.response?.data?.message || err?.message || 'Error al finalizar';
      const detalle = err?.response?.data?.detalle;
      const hojas = err?.response?.data?.hojasExistentes;

      if (code === 'SESIONES_INCOMPLETAS') {
        setSesionesIncompletas(detalle || []);
        setError(
          puedeForzar
            ? 'Hay sesiones sin completar. Marca "Forzar finalización" para continuar.'
            : 'Hay sesiones sin completar. Complétalas o solicita a un usuario autorizado que fuerce la finalización.'
        );
      } else if (code === 'LOTE_YA_FINALIZADO') {
        setHojasExistentes(hojas || []);
        setError('Este lote ya fue finalizado previamente. Las hojas existentes se listan arriba.');
      } else if (code === 'DEMASIADAS_OTS_EN_LOTE') {
        setError('Este lote afecta demasiadas OTs. Reduce el alcance del lote.');
      } else if (code === 'AUDIT_FAILED' || code === 'AVANCE_CONFLICT') {
        setError('Fallo parcial durante la finalización. El sistema intentará reconciliar automáticamente. Verifica en unos minutos.');
      } else {
        setError(message);
      }
    } finally {
      setFinalizando(false);
    }
  };

  if (!lotesCalibracion || lotesCalibracion.length === 0) {
    return null;
  }

  const primerLote = lotesCalibracion[0];
  const condiciones = primerLote.condicionesAmbientales || {};

  const handleClose = () => {
    if (finalizando) return;
    resetErrores();
    setOmitirValidacion(false);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Resumen y Finalización del Lote</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant={sesionesIncompletas ? 'warning' : 'danger'}>
            <FaExclamationTriangle className="me-2" />
            {error}
            {sesionesIncompletas && sesionesIncompletas.length > 0 && (
              <ul className="mt-2 mb-0 small">
                {sesionesIncompletas.slice(0, 5).map((s, i) => (
                  <li key={i}>
                    Lote {String(s.loteId).slice(-6)} — sesiones pendientes: {s.sesionesIncompletas.length}
                  </li>
                ))}
                {sesionesIncompletas.length > 5 && (
                  <li>… y {sesionesIncompletas.length - 5} más</li>
                )}
              </ul>
            )}
            {hojasExistentes && hojasExistentes.length > 0 && (
              <ul className="mt-2 mb-0 small">
                {hojasExistentes.map((h) => (
                  <li key={h._id}>Hoja #{h.consecutivo} (OT {String(h.ot).slice(-6)})</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <h6 className="mb-3">Información General</h6>
        <ListGroup className="mb-4" variant="flush">
          <ListGroup.Item>
            <strong>Total de equipos calibrados:</strong> {lotesCalibracion.length}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Plantilla utilizada:</strong> {plantilla?.codigo} - {plantilla?.descripcion}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Magnitudes calibradas:</strong>
            <div className="d-flex flex-wrap gap-1 mt-1">
              {(plantilla?.magnitudesConfig || []).map((m, idx) => (
                <Badge key={idx} bg="secondary">{m.nombre}</Badge>
              ))}
            </div>
          </ListGroup.Item>
        </ListGroup>

        <h6 className="mb-3">Condiciones Ambientales</h6>
        <ListGroup className="mb-4" variant="flush">
          <ListGroup.Item className="d-flex justify-content-between">
            <span><FaThermometerHalf className="text-danger me-2" /> Temperatura</span>
            <span>{condiciones.temperaturaMin}°C - {condiciones.temperaturaMax}°C</span>
          </ListGroup.Item>
          <ListGroup.Item className="d-flex justify-content-between">
            <span><FaTint className="text-info me-2" /> Humedad Relativa</span>
            <span>{condiciones.humedadMin}% - {condiciones.humedadMax}%</span>
          </ListGroup.Item>
        </ListGroup>

        <h6 className="mb-3">Estabilidad</h6>
        <ListGroup className="mb-4" variant="flush">
          {(plantilla?.magnitudesConfig || []).map((magnitud, idx) => {
            const sesion = primerLote.sesiones?.[idx];
            const estab = sesion?.estabilidad || {};
            const mediciones = estab.mediciones || [];
            const criterio = estab.criterio;
            const validos = mediciones.filter(m => m !== '' && !isNaN(m));
            const cumple = validos.length >= 2 && criterio
              ? (Math.max(...validos) - Math.min(...validos)) <= criterio
              : null;

            return (
              <ListGroup.Item key={idx} className="d-flex justify-content-between align-items-center">
                <span>{magnitud.nombre}</span>
                {cumple !== null && (
                  <Badge bg={cumple ? 'success' : 'danger'}>
                    {cumple ? <><FaCheckCircle className="me-1" /> CUMPLE</> : <><FaTimesCircle className="me-1" /> NO CUMPLE</>}
                  </Badge>
                )}
              </ListGroup.Item>
            );
          })}
        </ListGroup>

        <h6 className="mb-3">Equipos</h6>
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <ListGroup variant="flush">
            {lotesCalibracion.map((lote, idx) => {
              const eq = lote.equipoInOt || {};
              const snap = eq.snapshot || {};
              const completo = lote.estado === 'finalizado' || lote.estado === 'completado';
              return (
                <ListGroup.Item key={idx} className="d-flex justify-content-between align-items-center">
                  <span className="small">
                    {snap.item || eq.item?.nombre || '—'} • {snap.marca || eq.marca || ''} {snap.modelo || eq.modelo || ''} • S/N: {snap.serial || eq.serie || '—'}
                  </span>
                  <Badge bg={completo ? 'success' : 'warning'}>
                    {completo ? 'Completado' : 'Registrado'}
                  </Badge>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </div>

        {puedeForzar && (
          <div className="mt-3">
            <Form.Check
              type="checkbox"
              id="omitirValidacion"
              label="Forzar finalización aunque haya sesiones incompletas (queda auditado)"
              checked={omitirValidacion}
              onChange={(e) => setOmitirValidacion(e.target.checked)}
              disabled={finalizando}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={finalizando}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleFinalizar} disabled={finalizando}>
          {finalizando ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Finalizando...
            </>
          ) : (
            <>
              <FaCheckCircle className="me-2" />
              Confirmar y Finalizar
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ResumenFinalizacion;
